/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import cx from 'classnames';
import styles from './styles.module.scss';
import { Actionable, Carousel, Icon, Text, View } from '@az/starc-ui';
import Image from '@/components/NextImage';
import { FlexGrid } from '../FlexGrid/FlexGrid';
import { ProductCard } from './components/ProductCard/ProductCard';
import { MoreItems } from './components/MoreItems';
import { Close } from '@az/starc-ui-icons';
import { useActionCandidates } from './context/ActionCandidatesProvider';
import { type ActionCandidatesProps } from './interface';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

export const ActionCandidates = ({
  label,
  disableClose,
  isProductCountOverLimit,
  handleClose,
}: ActionCandidatesProps) => {
  const {
    isReviewCandidates,
    isBuyAgainCandidates,
    reviewCandidates,
    buyAgainCandidates,
    isOneProduct,
    formData,
  } = useActionCandidates();
  const enableBuyAgainRedesign = useForteDecisionFlagWithTrueDefault('enableBuyAgainRedesign');

  const productsLength =
    (isReviewCandidates ? reviewCandidates?.length : buyAgainCandidates?.length) ?? 0;

  const buyAgainIcon = 'https://www.autozone.com/cdn/images/B2C/US/logos/Icon-BuyAgain.svg';

  return (
    <View className={cx(styles.actionCandidatesContainer, { [styles.group]: !isOneProduct })}>
      <FlexGrid flexDirection="column" className={styles.actionCandidates}>
        <FlexGrid justifyContent="space-between">
          <View direction={'row'} align={'center'} gap={2}>
            {enableBuyAgainRedesign && isBuyAgainCandidates ? (
              <Image src={buyAgainIcon} alt={''} width={24} height={24} />
            ) : null}
            <Text variant="large-body" weight="bold" className={styles.actionCandidatesTitle}>
              {label}
            </Text>
          </View>
          {!disableClose ? (
            <Actionable
              onClick={handleClose}
              attributes={{ 'aria-label': 'Close Items Container' }}
            >
              <Icon svg={Close} size={6} />
            </Actionable>
          ) : null}
        </FlexGrid>
        <View>
          <Carousel className={styles.carousel} hideControls={productsLength < 3}>
            {isReviewCandidates
              ? reviewCandidates?.map((product) => (
                  <ProductCard
                    key={product.sku}
                    reviewCandidate={product}
                    formData={formData?.[product.sku]}
                  />
                ))
              : isBuyAgainCandidates
                ? buyAgainCandidates?.map((product) => (
                    <ProductCard key={product.skuId} buyAgainCandidate={product} />
                  ))
                : null}
            {isProductCountOverLimit ? <MoreItems /> : null}
          </Carousel>
        </View>
      </FlexGrid>
    </View>
  );
};
